/**************************************************/
/* Methods for PCA and PCVPCA based on wasm code  */
/* partly can be also used for PCR/PCVPCR         */
/**************************************************/

import { ismatrix, Matrix } from 'mdatools/arrays';
import { qchisq } from 'mdatools/distributions';

// zero level for distance parameters (if below, set Nu = 1)
const ZERO = Math.pow(10,-16);


/**
 * Returns length of array with PCA results.
 *
 * @param {number} nrows number of rows in a dataset.
 * @param {number} ncomp number of components in the model.
 *
 * @returns {number} the length of the results array.
 */
export function pca_getreslength(nrows, ncomp) {

   // Structure of PCA results:
   // - T: nr x ncomp
   // - H: nr x ncomp
   // - Q: nr x ncomp
   // - expvar: 1 x ncomp

   return 3 * nrows * ncomp + ncomp;
}


/**
 * Returns length of array with PCA model.
 *
 * @param {number} ncols number of columns in calibration set (number of variables).
 * @param {number} ncomp number of components
 *
 * @returns {number} the length of the model array.
 */
export function pca_getmodellength(ncols, ncomp) {

   // Structure of PCA model:
   // - mX: 1 x ncols
   // - sX: 1 x ncols
   // - P: ncols x ncomp
   // - eigenvals: 1 x ncomp
   // - Q_mean: 1 x ncomp
   // - Q_sd: 1 x ncomp
   // - Q_median: 1 x ncomp
   // - Q_iqr: 1 x ncomp
   // - H_mean: 1 x ncomp
   // - H_sd: 1 x ncomp
   // - H_median: 1 x ncomp
   // - H_iqr: 1 x ncomp

   return 2 * ncols + ncols * ncomp + 9 * ncomp;
}


/**
 * Returns part of PCA model array for given outcome.
 *
 * @param {TypedArray} m array with PCA model.
 * @param {string} name name of the outcome (e.g. 'mX', 'sX', 'loadings', etc.).
 * @param {number} a component number (for loadings).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function pcamodel_get(m, name, a) {

   if (!m || !m.class || !m.class.includes('pcamodel')) {
      throw new Error('pcamodel_get: parameter "m" must be JSON with PCA model.');
   }

   const ncols = m.ncols;
   const ncomp = m.ncomp;
   let offset, length;

   switch(name) {
      case 'mX':
         offset = 0;
         length = ncols;
         break;
      case 'sX':
         offset = ncols;
         length = ncols;
         break;
      case 'loadings':
         offset = 2 * ncols + (a - 1) * ncols;
         length = ncols;
         break;
      case 'eigenvals':
         offset = 2 * ncols + ncols * ncomp;
         length = ncomp;
         break;
      case 'qmean':
         offset = 2 * ncols + ncols * ncomp + ncomp;
         length = ncomp;
         break;
      case 'qsd':
         offset = 2 * ncols + ncols * ncomp + 2 * ncomp;
         length = ncomp;
         break;
      case 'qmedian':
         offset = 2 * ncols + ncols * ncomp + 3 * ncomp;
         length = ncomp;
         break;
      case 'qiqr':
         offset = 2 * ncols + ncols * ncomp + 4 * ncomp;
         length = ncomp;
         break;
      case 'hmean':
         offset = 2 * ncols + ncols * ncomp + 5 * ncomp;
         length = ncomp;
         break;
      case 'hsd':
         offset = 2 * ncols + ncols * ncomp + 6 * ncomp;
         length = ncomp;
         break;
      case 'hmedian':
         offset = 2 * ncols + ncols * ncomp + 7 * ncomp;
         length = ncomp;
         break;
      case 'hiqr':
         offset = 2 * ncols + ncols * ncomp + 8 * ncomp;
         length = ncomp;
         break;
      default:
         throw Error('pcamodel_get: wrong outcome name.');
   }

   return m.v.subarray(offset, offset + length);
}


/**
 * Returns distance parameters (u0, Nu) for PCA model.
 *
 * @param {Object} model a PCA model
 * @param {string} name name of distance ("q" or "h").
 * @param {number} a component number.
 * @param {string} type distance type ("classic" or "robust").
 *
 * @return {Object} JSON with two arrays u0 and Nu.
 */
export function pcamodel_get_distparams(m, name, type) {

   if (!m || !m.class || !m.class.includes('pcamodel')) {
      throw new Error('pcamodel_get_distparams: parameter "model" must be JSON with PCA model.');
   }

   if (!name) name = 'q';
   if (!type) type = 'classic';

   const Nu = new m.v.constructor(m.ncomp);

   if (type == 'robust') {
      const u0 = new m.v.constructor(m.ncomp);
      const Mu = pcamodel_get(m, name + 'median');
      const Su = pcamodel_get(m, name + 'iqr');

      for (let i = 0; i < m.ncomp; i++) {

         if (Su[i] < ZERO  ) {
            u0[i] = ZERO;
            Nu[i] = 1;
            continue;
         }

         const RM = Su[i] / Mu[i];
         let Nu_i;
         if (RM > 2.685592117) {
            Nu_i = 1;
         } else if (RM < 0.194565995) {
            Nu_i = 100;
         } else {
            Nu_i = Math.round(Math.exp(Math.pow(1.380948 * Math.log(2.68631 / RM), 1.185785)));
         }

         const a = qchisq(0.75, Nu_i) - qchisq(0.25, Nu_i);
         const b = qchisq(0.5, Nu_i);
         u0[i] = 0.5 * Nu_i * (Mu[i]/b + Su[i]/a);
         Nu[i] = Nu_i > 250 ? 250 : Nu_i;
      }

      return {u0, Nu};
   }

   const u0 = pcamodel_get(m, name + 'mean');
   const su = pcamodel_get(m, name + 'sd');
   for (let i = 0; i < m.ncomp; i++) {
      if (su[i] < ZERO) {
         Nu[i] = 1;
      } else {
         const Nu_l = 2 * (u0[i] / su[i]) ** 2;
         Nu[i] = Nu_l < 1 ? 1 : Nu_l > 250 ? 250 : Math.round(Nu_l);
      }
   }

   return {u0, Nu};
}


/**
 * Returns part of PCA result array for given outcome.
 *
 * @param {TypedArray} r array with PCA results.
 * @param {string} name name of the outcome (e.g. 'scores', 'hdist', 'qdist', 'expvar').
 * @param {number} a component number (for scores and distances).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function pcares_get(r, name, a) {

   if (!r || !r.class || !r.class.includes('pcares')) {
      throw new Error('pcares_get: parameter "r" must be JSON with PCA results.');
   }

   const nrows = r.nrows;
   const ncomp = r.ncomp;
   let offset, length;

   switch(name) {
      case 'scores':
         offset = (a - 1) * nrows;
         length = nrows;
         break;
      case 'hdist':
         offset = nrows * ncomp + (a - 1) * nrows;
         length = nrows;
         break;
      case 'qdist':
         offset = 2 * nrows * ncomp + (a - 1) * nrows;
         length = nrows;
         break;
      case 'cumexpvar':
         offset = 3 * nrows * ncomp;
         length = ncomp;
         break;
      default:
         throw new Error('pcares_get: wrong outcome name.');
   }

   return r.v.subarray(offset, offset + length);
}


/**
 * Creates a PCA model using wasm implementation.
 *
 * @param {Object} Module object with wasm module for PCVPCA.
 * @param {Matrix} X matrix with calibration set.
 * @param {number} ncomp number of components.
 * @param {boolean} center mean center the data columns or not.
 * @param {boolean} scale standardize the data columns or not.
 *
 * @returns {Object} JSON with PCA model.
 */
export function pca_fit(Module, X, ncomp, center, scale) {

   if (ncomp === undefined) {
      ncomp = Math.min(X.nrows - 1, X.ncols);
   }

   if (center === undefined) {
      center = true;
   }

   if (scale === undefined) {
      scale = false;
   }

   if (!ismatrix(X)) {
      throw new Error('pca_fit: X must be a matrix.');
   }

   if (X.nrows < 3 || X.ncols < 2) {
      throw new Error('pca_fit: X must have at least 3 rows and 2 columns.');
   }

   if (ncomp < 1 || ncomp > Math.min(X.nrows - 1, X.ncols)) {
      throw new Error('pca_fit: the value of "ncomp" does not match the dimension of X.');
   }

   // allocate memory in WASM heap space and copy dataset values
   const x_ptr = Module._malloc(X.v.length * X.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(X.v, x_ptr / X.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space for model and calibration set results outcomes
   const m_length = pca_getmodellength(X.ncols, ncomp);
   const m_constructor = Float64Array;
   const m_ptr = Module._malloc(m_length * m_constructor.BYTES_PER_ELEMENT);

   // call WASM function for PCA
   const res = Module.ccall('pcafit_wasm',
               ['number'], // return type
               ['number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
               [x_ptr, X.nrows, X.ncols, ncomp, center, scale, m_ptr]); // arguments

   if (res !== 1) {
      throw new Error('pca_fit: PCA failed.');
   }

   // copy part of HEAP memory with model outcomes
   const model = Module.HEAPF64.slice(
      m_ptr/m_constructor.BYTES_PER_ELEMENT,
      m_ptr/m_constructor.BYTES_PER_ELEMENT + m_length
   );

   // free the memory
   Module._free(x_ptr);
   Module._free(m_ptr);

   // return PCA model and result objects
   return {
      class: ['pcamodel'],
      v: model,
      ncomp: ncomp,
      nrows: X.nrows,
      ncols: X.ncols,
      center: center,
      scale: scale
   };
}


/**
 * Projects dataset to a PCA model.
 *
 * @param {Object} Module object with wasm module for PCVPCA.
 * @param {Object} m JSON with PCA model created by `pca_fit()`.
 * @param {Matrix} X matrix with dataset.
 *
 * @returns {Object} JSON with PCA results.
 */
export function pca_predict(Module, m, X) {

   if (!m || !m.class || !m.class.includes('pcamodel')) {
      throw new Error('pca_predict: parameter "m" must be JSON with PCA results.');
   }

   if (!ismatrix(X)) {
      throw new Error('pca_predict: X must be a matrix.');
   }

   if (X.nrows < 1) {
      throw new Error('pca_fit: X must have at least 1 row.');
   }

   if (X.ncols !== m.ncols) {
      throw new Error('pca_fit: X must have the same number of columns as the training set.');
   }

   // allocate memory in WASM heap space and copy dataset values
   const x_ptr = Module._malloc(X.v.length * X.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(X.v, x_ptr / X.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space and copy model values
   const m_ptr = Module._malloc(m.v.length * m.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(m.v, m_ptr / m.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space for result values
   const r_length = pca_getreslength(X.nrows, m.ncomp);
   const r_constructor = Float64Array;
   const r_ptr = Module._malloc(r_length * r_constructor.BYTES_PER_ELEMENT);

   // call WASM function for PCA prediction
   const res = Module.ccall('pcapredict_wasm',
               ['number'], // return type
               ['number', 'number', 'number', 'number', 'number', 'number'], // argument types
               [x_ptr, X.nrows, X.ncols, m.ncomp, m_ptr, r_ptr]); // arguments

   if (res !== 1) {
      throw new Error('pca_predict: PCA prediction failed.');
   }

   // copy part of HEAP memory with result values
   const res_values = Module.HEAPF64.slice(
      r_ptr/r_constructor.BYTES_PER_ELEMENT,
      r_ptr/r_constructor.BYTES_PER_ELEMENT + r_length
   );

   // free the memory
   Module._free(x_ptr);
   Module._free(m_ptr);
   Module._free(r_ptr);

   // return PCA results
   return {
      class: ['pcares'],
      v: res_values,
      ncomp: m.ncomp,
      nrows: X.nrows,
      ncols: X.ncols,
      center: m.center,
      scale: m.scale
   };
}


/**
 * Create a PV-set based on PCA decomposition.
 *
 * @param {Object} Module object with wasm module for PCVPCA.
 * @param {Object} m JSON with PCA model created by `pca_fit()`.
 * @param {Matrix} X matrix with dataset.
 * @param {number} ncomp number of components for PV-set generation.
 * @param {number} cv_nseg number of segments for cross-validation.
 * @param {number} cv_type type of cross-validation (0: loo, 1: ven, 2: rand).
 * @param {number} cv_scope scope of cross-validation (0: global, 1: local).
 *
 * @returns {Matrix} the generated PV-set.
 */
export function pcvpca(Module, m, X, ncomp, cv_nseg, cv_type, cv_scope) {

   if (!m || !m.class || !m.class.includes('pcamodel')) {
      throw new Error('pcapcv: parameter "m" must be JSON with PCA results.');
   }

   if (!ismatrix(X)) {
      throw new Error('pcapcv: X must be a matrix.');
   }

   if (X.nrows < 3) {
      throw new Error('pcapcv: X must have at least 3 rows.');
   }

   if (X.ncols !== m.ncols) {
      throw new Error('pcapcv: X must have the same number of columns as the training set.');
   }

   if (!Number.isInteger(cv_type) || ![0, 1, 2].includes(cv_type)) {
      throw new Error('pcapcv: parameter "cv_type" must be one of 0 ("loo"), 1 ("ven"), 2 ("rand).');
   }

   if (!Number.isInteger(cv_scope) || ![0, 1].includes(cv_scope)) {
      throw new Error('pcapcv: parameter "cv_scope" must be 0 ("global") or 1 ("local").');
   }

   if (cv_type > 0 && (!Number.isInteger(cv_nseg) || cv_nseg < 2)) {
      throw new Error('pcapcv: parameter "cv_nseg" must be a number ≥ 2.');
   }

   if (cv_type > 0 && cv_nseg > X.nrows) {
      throw new Error('pcapcv: the value of "cv_nseg" is too large for the given X.');
   }

   if (!Number.isInteger(ncomp) || ncomp < 1) {
      throw new Error('pcapcv: the value of "ncomp" must be a number >= 1.');
   }

   const max_ncomp = cv_type === 0 ?
      Math.min(X.nrows - 2, X.ncols, m.ncomp) :
      Math.min(X.nrows - Math.ceil(X.nrows / cv_nseg) - 1, X.ncols, m.ncomp);

   if (ncomp > max_ncomp) {
      throw new Error('pcapcv: the value of "ncomp" is too large.');
   }


   // allocate memory in WASM heap space and copy dataset values
   const x_ptr = Module._malloc(X.v.length * X.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(X.v, x_ptr / X.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space and copy model values
   const m_ptr = Module._malloc(m.v.length * m.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(m.v, m_ptr / m.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space for Xpv values
   const xpv_length = X.v.length;
   const xpv_constructor = X.v.constructor;
   const xpv_ptr = Module._malloc(xpv_length * xpv_constructor.BYTES_PER_ELEMENT);

   // call WASM function for PCAPCV
   const res = Module.ccall('pcvpca_wasm',
               ['number'], // return type
               ['number', 'number', 'number', 'number', 'number', 'number',
               'number', 'number', 'number', 'number', 'number'], // argument types
               [x_ptr, X.nrows, X.ncols, ncomp, m.center, m.scale,
               cv_type, cv_nseg, cv_scope, m_ptr, xpv_ptr]); // arguments

   if (res !== 1) {
      throw new Error('pcapcv: PCVPCA failed.');
   }

   // copy part of HEAP memory with Xpv values
   const xpv = Module.HEAPF64.slice(
      xpv_ptr/xpv_constructor.BYTES_PER_ELEMENT,
      xpv_ptr/xpv_constructor.BYTES_PER_ELEMENT + xpv_length
   );

   // free the memory
   Module._free(x_ptr);
   Module._free(m_ptr);
   Module._free(xpv_ptr);

   // return Xpv
   return new Matrix(xpv, X.nrows, X.ncols);
}
